// Gestion de usuarios (SOLO admin): crear usuarios con permisos por modulo,
// editar permisos, activar/desactivar y resetear claves.

import { api } from "../api";
import { iconoLapiz, iconoMas } from "../iconos";
import type { Usuario } from "../../lib/types";

const MODULOS: Array<{ clave: string; nombre: string }> = [
  { clave: "pedido", nombre: "Pedido" },
  { clave: "pedidos_editar", nombre: "Editar pedidos" },
  { clave: "pedidos_anular", nombre: "Anular pedidos" },
  { clave: "produccion", nombre: "Produccion" },
  { clave: "cuentas", nombre: "Cuentas" },
  { clave: "listas", nombre: "Lista de precios" },
  { clave: "subas", nombre: "Subas" },
  { clave: "informes", nombre: "Informes" },
  { clave: "clientes", nombre: "Clientes" },
  { clave: "productos", nombre: "Productos" },
  { clave: "categorias", nombre: "Categorias" },
];

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

function permisosDe(u: Usuario): string[] {
  try {
    const p: unknown = JSON.parse(u.permisos || "[]");
    return Array.isArray(p) ? p.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function grillaPermisos(idPrefix: string, marcados: string[]): string {
  return `<div class="grid grid-cols-2 gap-1 sm:grid-cols-3" data-grid-permisos>
    ${MODULOS.map(
      (m) => `<label class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-crema-50">
        <input type="checkbox" value="${m.clave}" id="${idPrefix}_${m.clave}" class="h-4 w-4 accent-masa-600" ${marcados.includes(m.clave) ? "checked" : ""} />
        <span>${m.nombre}</span></label>`,
    ).join("")}
  </div>`;
}

export async function renderUsuarios(contenedor: HTMLElement): Promise<void> {
  let usuarios: Usuario[] = [];

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Usuarios</h1>
      <p class="subtitulo">Crea usuarios y elegi que puede hacer cada uno. Solo el administrador ve esta pantalla.</p>
      <p class="mt-2 text-sm text-masa-500">
        <b>Editar pedidos</b> deja corregir un pedido ya confirmado (cantidades, articulos, fecha)
        sin poder cambiar precios ni la lista del cliente. <b>Anular pedidos</b> es aparte: da de
        baja el pedido. Los dos necesitan tambien el permiso <b>Pedido</b> para entrar a la pantalla.
      </p>
    </div>

    <details class="tarjeta mb-4">
      <summary class="flex cursor-pointer list-none items-center gap-2 text-sm font-bold uppercase tracking-wide text-masa-600">
        <span class="text-masa-500">${iconoMas}</span> Nuevo usuario
      </summary>
      <form id="f" class="mt-4 space-y-3">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label><span class="etiqueta">Usuario</span><input id="n_usuario" class="campo" placeholder="ej: repartidor" /></label>
          <label><span class="etiqueta">Clave (min 8)</span><input id="n_clave" type="password" class="campo" autocomplete="new-password" /></label>
          <label class="flex items-end gap-2 pb-2">
            <input id="n_admin" type="checkbox" class="h-4 w-4 accent-masa-600" />
            <span class="text-sm font-medium">Es administrador (ve todo)</span></label>
        </div>
        <div id="n_permisos_wrap">
          <span class="etiqueta">Permisos (que puede usar)</span>
          ${grillaPermisos("n", [])}
        </div>
        <div class="flex items-center gap-3">
          <button class="btn-primario">${iconoMas} Crear usuario</button>
          <span id="n_err" class="text-sm text-red-600"></span>
        </div>
      </form>
    </details>

    <div class="tarjeta overflow-x-auto p-0">
      <table class="tabla">
        <thead><tr><th>Usuario</th><th>Tipo</th><th>Permisos</th><th>Estado</th><th class="text-right">Acciones</th></tr></thead>
        <tbody id="tbody"></tbody>
      </table>
    </div>
    <dialog id="dlg"></dialog>`;

  const tbody = contenedor.querySelector("#tbody") as HTMLElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;
  const nErr = contenedor.querySelector("#n_err") as HTMLElement;
  const nAdmin = contenedor.querySelector("#n_admin") as HTMLInputElement;
  const nPermisosWrap = contenedor.querySelector("#n_permisos_wrap") as HTMLElement;

  nAdmin.addEventListener("change", () => {
    nPermisosWrap.classList.toggle("opacity-40", nAdmin.checked);
    nPermisosWrap.querySelectorAll<HTMLInputElement>("input").forEach((c) => (c.disabled = nAdmin.checked));
  });

  function pintar(): void {
    tbody.innerHTML = usuarios
      .map((u) => {
        const perms = u.es_admin ? "todos" : permisosDe(u).map((p) => MODULOS.find((m) => m.clave === p)?.nombre ?? p).join(", ") || "ninguno";
        return `<tr class="${u.activo ? "" : "opacity-50"}">
          <td class="font-medium">${esc(u.usuario)}</td>
          <td>${u.es_admin ? `<span class="rounded-full bg-masa-100 px-2 py-0.5 text-xs font-semibold text-masa-800">ADMIN</span>` : `<span class="text-sm text-masa-500">usuario</span>`}</td>
          <td class="max-w-[16rem] text-sm text-masa-600">${esc(perms)}</td>
          <td>${u.activo ? "Activo" : "Desactivado"}</td>
          <td class="text-right"><button class="chip-accion" data-editar="${u.id}" title="Editar">${iconoLapiz}</button></td>
        </tr>`;
      })
      .join("");
  }

  async function cargar(): Promise<void> {
    tbody.innerHTML = `<tr><td colspan="5" class="py-6 text-center text-masa-400">Cargando...</td></tr>`;
    usuarios = await api.get<Usuario[]>("/api/usuarios");
    pintar();
  }

  const form = contenedor.querySelector("#f") as HTMLFormElement;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    nErr.textContent = "";
    const usuario = (contenedor.querySelector("#n_usuario") as HTMLInputElement).value.trim();
    const clave = (contenedor.querySelector("#n_clave") as HTMLInputElement).value;
    const esAdmin = nAdmin.checked;
    const permisos = [...nPermisosWrap.querySelectorAll<HTMLInputElement>("input:checked")].map((c) => c.value);
    if (usuario.length < 3) { nErr.textContent = "El usuario debe tener al menos 3 caracteres"; return; }
    if (clave.length < 8) { nErr.textContent = "La clave debe tener al menos 8 caracteres"; return; }
    if (!esAdmin && permisos.length === 0) { nErr.textContent = "Elegi al menos un permiso (o marca admin)"; return; }
    try {
      await api.post("/api/usuarios", { usuario, clave, es_admin: esAdmin, permisos });
      form.reset();
      nAdmin.dispatchEvent(new Event("change"));
      await cargar();
    } catch (e) {
      nErr.textContent = e instanceof Error ? e.message : "Error";
    }
  });

  tbody.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest("[data-editar]");
    if (!btn) return;
    const u = usuarios.find((x) => x.id === Number(btn.getAttribute("data-editar")));
    if (u) abrirEdicion(u);
  });

  function abrirEdicion(u: Usuario): void {
    dlg.innerHTML = `
      <form method="dialog" class="tarjeta w-[30rem] max-w-full border-0 shadow-none">
        <h2 class="mb-1 text-lg font-bold">Editar usuario</h2>
        <p class="mb-4 text-sm text-masa-500">${esc(u.usuario)}</p>
        <div class="space-y-3">
          <label class="flex items-center gap-2">
            <input id="e_admin" type="checkbox" class="h-4 w-4 accent-masa-600" ${u.es_admin ? "checked" : ""} />
            <span class="text-sm font-medium">Es administrador (ve todo)</span></label>
          <div id="e_permisos_wrap" class="${u.es_admin ? "opacity-40" : ""}">
            <span class="etiqueta">Permisos</span>
            ${grillaPermisos("e", permisosDe(u))}
          </div>
          <label class="flex items-center gap-2">
            <input id="e_activo" type="checkbox" class="h-4 w-4 accent-masa-600" ${u.activo ? "checked" : ""} />
            <span class="text-sm">Activo (puede entrar al sistema)</span></label>
          <label class="block"><span class="etiqueta">Resetear clave (opcional, min 8)</span>
            <input id="e_clave" type="password" class="campo" placeholder="Dejar vacio para no cambiarla" autocomplete="new-password" /></label>
        </div>
        <p id="e_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex justify-end gap-2">
          <button value="cancel" class="btn-sec">Cancelar</button>
          <button id="e_guardar" value="ok" class="btn-primario">Guardar</button>
        </div>
      </form>`;
    const eErr = dlg.querySelector("#e_err") as HTMLElement;
    const eAdmin = dlg.querySelector("#e_admin") as HTMLInputElement;
    const eWrap = dlg.querySelector("#e_permisos_wrap") as HTMLElement;
    eAdmin.addEventListener("change", () => {
      eWrap.classList.toggle("opacity-40", eAdmin.checked);
      eWrap.querySelectorAll<HTMLInputElement>("input").forEach((c) => (c.disabled = eAdmin.checked));
    });
    if (u.es_admin) eWrap.querySelectorAll<HTMLInputElement>("input").forEach((c) => (c.disabled = true));

    dlg.querySelector("#e_guardar")?.addEventListener("click", async (ev) => {
      ev.preventDefault();
      eErr.textContent = "";
      const claveNueva = (dlg.querySelector("#e_clave") as HTMLInputElement).value;
      if (claveNueva && claveNueva.length < 8) { eErr.textContent = "La clave debe tener al menos 8 caracteres"; return; }
      const permisos = [...eWrap.querySelectorAll<HTMLInputElement>("input:checked")].map((c) => c.value);
      try {
        await api.put(`/api/usuarios/${u.id}`, {
          es_admin: eAdmin.checked,
          permisos,
          activo: (dlg.querySelector("#e_activo") as HTMLInputElement).checked ? 1 : 0,
          clave_nueva: claveNueva || null,
        });
        dlg.close();
        await cargar();
      } catch (e) {
        eErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });
    dlg.showModal();
  }

  await cargar();
}
